(() => {
  const storageKey = 'wob.collectionExperimentIdentity.v1';
  const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
  let cards = new WeakMap();
  let consentGeneration = 0;
  const consented = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  // Shopify's customer privacy API loads asynchronously. Deciding enrollment
  // before it exists would read as "no consent" and silently exclude consented
  // shoppers, so request it now and wait briefly before the first request.
  const privacyApiLoaded = () => typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function';
  const privacyReady = new Promise(resolve => {
    if (privacyApiLoaded()) { resolve(); return; }
    document.addEventListener('shopifyCustomerPrivacyApiLoaded', () => resolve(), { once: true });
    try { window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], () => resolve()); } catch { /* The timeout below still resolves. */ }
    const started = Date.now();
    const poll = () => { if (privacyApiLoaded() || Date.now() - started >= 1000) resolve(); else window.setTimeout(poll, 50); };
    poll();
  });
  const claimsFor = token => {
    if (typeof token !== 'string' || token.length > 1536) throw new Error('invalid-experiment-context');
    return JSON.parse(atob(token.split('.')[0].replace(/-/g, '+').replace(/_/g, '/')));
  };
  const identity = () => {
    if (!consented() || !window.crypto?.randomUUID) return null;
    try {
      const saved = JSON.parse(window.localStorage.getItem(storageKey) || 'null');
      const id = saved && uuid.test(saved.id) && saved.expires > Date.now() ? saved.id : window.crypto.randomUUID();
      window.localStorage.setItem(storageKey, JSON.stringify({ id, expires: Date.now() + 90 * 86400000 }));
      return id;
    } catch { return null; }
  };
  const publish = (name, context, extra = {}) => {
    if (!consented() || typeof window.Shopify?.analytics?.publish !== 'function') throw new Error('experiment-telemetry-unavailable');
    Promise.resolve(window.Shopify.analytics.publish(name, { schema_version: 1, worker_target: 'web', experiment_token: context.token, ...extra })).catch(() => {});
  };
  const create = root => {
    let visitId = null, assignmentId = null, accepted = null, rendered = false;
    let generation = consentGeneration;
    return {
      // Resolves at once when the API is loaded or this collection has no test flag.
      ready() {
        return root.dataset.collectionExperimentsEnabled === 'true' ? privacyReady : Promise.resolve();
      },
      request(sortKey, filters) {
        if (generation !== consentGeneration) {
          visitId = null; assignmentId = null; accepted = null; rendered = false; generation = consentGeneration;
        }
        if (root.dataset.collectionExperimentsEnabled !== 'true' || sortKey !== 'recommended'
            || root.dataset.currencyCode !== 'USD' || !consented() || typeof window.Shopify?.analytics?.publish !== 'function'
            || Object.values(filters || {}).some(value => Array.isArray(value) ? value.length : !!value)) return null;
        assignmentId = assignmentId || identity();
        if (!assignmentId) return null;
        visitId = visitId || window.crypto.randomUUID();
        return { schemaVersion: 1, surface: 'collection_grid', currency: 'USD', assignmentId, visitId, consentEligible: true };
      },
      accept(payload) {
        const context = payload?.experiment;
        if (!context) { if (accepted) throw new Error('experiment-ended'); return; }
        if (!consented() || generation !== consentGeneration || !visitId || root.dataset.collectionExperimentsEnabled !== 'true') throw new Error('experiment-not-eligible');
        const claims = claimsFor(context.token);
        if (context.schemaVersion !== 1 || context.visitId !== visitId || !['A', 'B'].includes(context.variant)
            || claims.r !== context.revision || claims.a !== context.variant || claims.visit !== visitId
            || claims.collection !== root.dataset.collectionId || claims.exp * 1000 <= Date.now()) throw new Error('experiment-response-mismatch');
        if (accepted && (accepted.revision !== context.revision || accepted.variant !== context.variant)) throw new Error('experiment-arm-changed');
        if (!accepted) publish('wob:collection_experiment_visit', context);
        accepted = context;
      },
      bindCards(fragment, context) {
        if (context) fragment.querySelectorAll('.product-card').forEach(card => cards.set(card, context));
      },
      rendered(success) {
        if (accepted && !rendered) {
          rendered = true;
          try { publish('wob:collection_experiment_render', accepted, { render_state: success ? 'success' : 'failure' }); } catch { /* Consent may have changed. */ }
        }
      }
    };
  };
  window.WobCollectionExperiments = Object.freeze({
    schemaVersion: 1,
    assignmentAvailable: true,
    create,
    contextFor(element) {
      if (!consented()) return null;
      const context = cards.get(element?.closest?.('.product-card'));
      if (!context) return null;
      try { return claimsFor(context.token).exp * 1000 > Date.now() ? context.token : null; } catch { return null; }
    },
    assertCompatibleResponse(payload) {
      if (payload?.experiment != null) {
        throw new Error('experiment-requires-visit-validation');
      }
    }
  });
  const consentChanged = () => {
    if (!consented()) {
      consentGeneration += 1;
      cards = new WeakMap();
      try { window.localStorage.removeItem(storageKey); } catch { /* Storage may be unavailable. */ }
    }
  };
  document.addEventListener('visitorConsentCollected', consentChanged);
  document.addEventListener('wob:termly-consent-synced', consentChanged);
})();
